function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { Component } from 'react';
import memoize from 'memoize-one';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ContextActions, GLOBAL_SHORTCUTS } from '@deephaven/components';
import { dhFileCsv, dhFileDownload, dhFileSpreadsheet, vsClippy, vsFileZip, vsTrash, vsWarning } from '@deephaven/icons';
import "./CsvOverlay.css";
import { assertNotNull, TextUtils } from '@deephaven/utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var PASTED_VALUES = 'pasted values';
var INVALID_MIME_TYPES = [/^audio.*/, /^font.*/, /^image.*/, /^video.*/];
var VALID_EXTENSIONS = ['.csv', '.tsv', '.tab', '.psv', '.dsv', '.txt'];
var ZIP_EXTENSIONS = ['.zip'];

/**
 * Overlay that is displayed when uploading a CSV file.
 */
class CsvOverlay extends Component {
  static isValidDropItem(item) {
    return item != null && item.kind === 'file' && !INVALID_MIME_TYPES.find(invalid => invalid.test(item.type));
  }
  static isValidExtension(name) {
    var allowZip = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    return VALID_EXTENSIONS.some(ext => name.endsWith(ext)) || allowZip && ZIP_EXTENSIONS.some(ext => name.endsWith(ext));
  }
  static handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  static getIcon(fileName) {
    if (fileName === PASTED_VALUES) {
      return vsClippy;
    }
    if (fileName.endsWith('.csv')) {
      return dhFileCsv;
    }
    if (fileName.endsWith('.zip')) {
      return vsFileZip;
    }
    return dhFileSpreadsheet;
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "fileElem", void 0);
    _defineProperty(this, "divElem", void 0);
    _defineProperty(this, "getValidExtensions", memoize(allowZip => allowZip ? [...VALID_EXTENSIONS, ...ZIP_EXTENSIONS] : [...VALID_EXTENSIONS]));
    _defineProperty(this, "getAcceptString", memoize(allowZip => this.getValidExtensions(allowZip).join(', ')));
    _defineProperty(this, "getFileTypeErrorString", memoize(allowZip => TextUtils.join(this.getValidExtensions(allowZip), 'or')));
    this.handleSelectFile = this.handleSelectFile.bind(this);
    this.handleFiles = this.handleFiles.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.unstageFile = this.unstageFile.bind(this);
    this.handleMenuPaste = this.handleMenuPaste.bind(this);
    this.handlePasteEvent = this.handlePasteEvent.bind(this);
    this.fileElem = /*#__PURE__*/React.createRef();
    this.divElem = /*#__PURE__*/React.createRef();
    this.state = {
      selectedFileName: ''
    };
  }
  componentDidMount() {
    var _this$divElem$current, _this$divElem$current2;
    (_this$divElem$current = this.divElem.current) === null || _this$divElem$current === void 0 || _this$divElem$current.addEventListener('paste', this.handlePasteEvent);
    (_this$divElem$current2 = this.divElem.current) === null || _this$divElem$current2 === void 0 || _this$divElem$current2.focus();
  }
  componentWillUnmount() {
    var _this$divElem$current3;
    (_this$divElem$current3 = this.divElem.current) === null || _this$divElem$current3 === void 0 || _this$divElem$current3.removeEventListener('paste', this.handlePasteEvent);
  }
  handleSelectFile() {
    if (this.fileElem.current) {
      var _this$fileElem$curren;
      this.fileElem.current.value = '';
      (_this$fileElem$curren = this.fileElem.current) === null || _this$fileElem$curren === void 0 || _this$fileElem$curren.click();
    }
  }
  handleFiles(event) {
    event.stopPropagation();
    event.preventDefault();
    var files = event.target.files;
    if (files != null) {
      this.handleFile(files[0]);
    }
  }
  handleDrop(e) {
    var _this$props = this.props,
      allowZip = _this$props.allowZip,
      clearDragError = _this$props.clearDragError,
      dragError = _this$props.dragError;
    e.preventDefault();
    e.stopPropagation();
    if (dragError != null) {
      clearDragError();
      return;
    }
    if (e.dataTransfer == null || e.dataTransfer.items == null || e.dataTransfer.items.length === 0) {
      return;
    }
    var file = e.dataTransfer.items[0].getAsFile();
    assertNotNull(file);
    if (CsvOverlay.isValidExtension(file.name, allowZip)) {
      this.handleFile(file);
    } else {
      this.setState({
        dropError: CsvOverlay.FILE_TYPE_ERROR
      });
    }
  }
  unstageFile(event) {
    var onCancel = this.props.onCancel;
    event.stopPropagation();
    event.preventDefault();
    onCancel();
    this.setState({
      selectedFileName: '',
      dropError: undefined
    });
  }
  handleFile(file) {
    var onFileOpened = this.props.onFileOpened;
    onFileOpened(file);
    this.setState({
      selectedFileName: file.name,
      dropError: undefined
    });
  }
  handleMenuPaste() {
    var _this$props2 = this.props,
      onPaste = _this$props2.onPaste,
      onError = _this$props2.onError,
      uploadInProgress = _this$props2.uploadInProgress;
    if (uploadInProgress) {
      return;
    }
    navigator.clipboard.readText().then(clipText => {
      onPaste(clipText);
      this.setState({
        selectedFileName: PASTED_VALUES,
        dropError: undefined
      });
    }).catch(e => onError(e));
  }
  handlePasteEvent(event) {
    event.stopPropagation();
    event.preventDefault();
    this.handleMenuPaste();
  }
  makeContextMenuItems() {
    var uploadInProgress = this.props.uploadInProgress;
    return [{
      title: 'Paste',
      icon: vsClippy,
      action: this.handleMenuPaste,
      shortcut: GLOBAL_SHORTCUTS.PASTE,
      disabled: uploadInProgress
    }];
  }
  render() {
    var _this$props3 = this.props,
      allowZip = _this$props3.allowZip,
      dragError = _this$props3.dragError,
      uploadInProgress = _this$props3.uploadInProgress;
    var _this$state = this.state,
      selectedFileName = _this$state.selectedFileName,
      dropError = _this$state.dropError;
    var error = dragError !== null && dragError !== void 0 ? dragError : dropError;
    var contextActions = this.makeContextMenuItems();
    return /*#__PURE__*/_jsxs("div", {
      ref: this.divElem,
      className: "csv-overlay fill-parent-absolute",
      onDragOver: CsvOverlay.handleDragOver,
      onDrop: this.handleDrop,
      tabIndex: -1,
      children: [/*#__PURE__*/_jsx(ContextActions, {
        actions: contextActions
      }), /*#__PURE__*/_jsx("input", {
        ref: this.fileElem,
        type: "file",
        id: "fileElem",
        accept: this.getAcceptString(allowZip),
        style: {
          display: 'none'
        },
        onChange: this.handleFiles,
        "data-testid": "fileElem"
      }), !selectedFileName && (error === undefined || error === '') && /*#__PURE__*/_jsxs("div", {
        className: "message-content",
        children: [/*#__PURE__*/_jsx("div", {
          className: "message-icon",
          children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
            icon: dhFileDownload
          })
        }), /*#__PURE__*/_jsx("label", {
          children: "Drop file (or paste values) here to import"
        }), /*#__PURE__*/_jsx("div", {
          className: "message-small",
          children: /*#__PURE__*/_jsx("label", {
            children: "or"
          })
        }), /*#__PURE__*/_jsx(Button, {
          kind: "primary",
          onClick: this.handleSelectFile,
          children: "Select File..."
        })]
      }), selectedFileName && (error === undefined || error === '') && /*#__PURE__*/_jsxs("div", {
        className: "selected-content",
        children: [/*#__PURE__*/_jsx("div", {
          className: "selected-icon",
          children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
            icon: CsvOverlay.getIcon(selectedFileName)
          })
        }), /*#__PURE__*/_jsxs("div", {
          className: "selected-text",
          children: [/*#__PURE__*/_jsx("label", {
            children: "Selected File"
          }), /*#__PURE__*/_jsxs("label", {
            className: "selected-name",
            children: [selectedFileName, !uploadInProgress && /*#__PURE__*/_jsx(Button, {
              kind: "ghost",
              className: "ml-2",
              onClick: this.unstageFile,
              icon: vsTrash,
              tooltip: "Remove"
            })]
          })]
        })]
      }), Boolean(error) && /*#__PURE__*/_jsxs("div", {
        className: "message-content",
        children: [/*#__PURE__*/_jsx("div", {
          className: "message-icon",
          children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
            icon: vsWarning
          })
        }), /*#__PURE__*/_jsx("label", {
          children: error
        }), error === CsvOverlay.FILE_TYPE_ERROR && /*#__PURE__*/_jsx("div", {
          className: "message-small",
          children: /*#__PURE__*/_jsx("label", {
            children: this.getFileTypeErrorString(allowZip)
          })
        })]
      })]
    });
  }
}
_defineProperty(CsvOverlay, "defaultProps", {
  allowZip: false,
  dragError: null
});
_defineProperty(CsvOverlay, "MULTIPLE_FILE_ERROR", 'Please select only one file');
_defineProperty(CsvOverlay, "FILE_TYPE_ERROR", 'Filetype not supported.');
export default CsvOverlay;
//# sourceMappingURL=CsvOverlay.js.map